import { useEffect } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { MenuType } from 'api/models';
import { useGetOrderingDeadlinesApi } from 'hooks/useGetOrderingDeadlinesApi';
import { useTelegramUser } from 'hooks/useTelegramUser';
import { useGetOrderApi } from 'pages/Week/hooks/useGetOrderApi';
import { getNearestOrderDay } from 'pages/Week/utils';
import { ROUTES } from 'utils/routes';
import { toBusinessDateTime } from 'utils/toBusinessDateTime';

// Module scope is evaluated once per page load, so these describe the current load: where the app
// started, and whether the week has already had its one chance to forward. A reload starts over.
const startPathname = window.location.pathname;
let isEntryHandled = false;

/**
 * When a page load starts on the week, forwards to the nearest day that can still be ordered: its
 * checkout if that day is already ordered, its menu otherwise. Returns true while deciding.
 */
export const useOpenNearestDayOnEntry = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: userId } = useTelegramUser();

  // Coming back to the week later in the same load ("Назад") must not bounce the user forward again.
  const isEntry = !isEntryHandled && location.pathname === startPathname;

  const deadlines = useGetOrderingDeadlinesApi(isEntry);
  const zone = deadlines.data?.data.zone;
  const deadline = deadlines.data?.data.unavailableAt[MenuType.Dinner];
  const dayDate =
    isEntry && zone && deadline
      ? getNearestOrderDay(toBusinessDateTime(new Date(), zone), deadline)
      : undefined;

  const { orderResponse } = useGetOrderApi(dayDate);
  const orderId = orderResponse.data?.data.id;
  const hasNoOrder =
    (orderResponse.error as AxiosError)?.response?.status === 404;
  const failed =
    deadlines.isError ||
    (deadlines.isSuccess && !deadline) ||
    (orderResponse.isError && !hasNoOrder);

  useEffect(() => {
    if (isEntry && failed) {
      isEntryHandled = true;
    }
  }, [isEntry, failed]);

  useEffect(() => {
    if (!dayDate) {
      return;
    }

    if (orderId) {
      isEntryHandled = true;
      navigate(
        generatePath(ROUTES.CHECKOUT_WITH_ORDER, {
          userId: `${userId}`,
          dayDate,
          orderId: `${orderId}`,
        }),
        { replace: true },
      );
    } else if (hasNoOrder) {
      isEntryHandled = true;
      navigate(
        generatePath(ROUTES.MENU_CURRENT_DAY, { userId: `${userId}`, dayDate }),
        { replace: true },
      );
    }
  }, [dayDate, orderId, hasNoOrder]);

  return isEntry && !failed;
};
